/**
 * Experiment Types
 *
 * Types and utilities for the experimentation system, including
 * event-based horizon constants and helpers, and the canonical
 * fixed-horizon menu per bar duration.
 */
// ============================================================================
// Event Horizon Constants
// ============================================================================
/**
 * Event-based horizon sentinel values.
 *
 * CONVENTION:
 * - horizonSeconds < 2,000,000,000: offset-based (duration in seconds from entry)
 * - horizonSeconds >= 2,000,000,000: event-based (sentinel for specific market event)
 *
 * We use large positive values instead of negative values because the database
 * column `experiment_entry_results.horizonSeconds` is INT UNSIGNED.
 *
 * These are the single source of truth for event horizons.
 * All other code should import from this module.
 */
/**
 * End of Day horizon: Performance at market close (3:59pm EST).
 * Uses regular market hours, not extended session.
 * Value is larger than NDO so descending sort puts EOD first.
 */
export const HORIZON_END_OF_DAY = 2147483647;
/**
 * Next Day Open horizon: Performance at next trading day open (9:32am EST).
 * Uses regular market hours, not pre-market session.
 */
export const HORIZON_NEXT_DAY_OPEN = 2147483646;
// ============================================================================
// Event Horizon Helpers
// ============================================================================
/**
 * Type guard for event-based horizons.
 * Use this instead of raw comparisons for:
 * - Explicit, self-documenting code
 * - Future-proofing if we add more event horizons
 * - Consistent behavior across the codebase
 *
 * @param horizonSeconds - The horizon value to check
 * @returns true if this is an event-based horizon (EOD or NDO)
 */
export function isEventHorizon(horizonSeconds) {
    return horizonSeconds === HORIZON_END_OF_DAY || horizonSeconds === HORIZON_NEXT_DAY_OPEN;
}
/**
 * Get display label for event horizons.
 * Returns null for offset horizons (use formatTimeHorizon instead).
 *
 * @param horizonSeconds - The horizon value
 * @returns 'EOD', 'Next Open', or null for offset horizons
 */
export function getEventHorizonLabel(horizonSeconds) {
    if (horizonSeconds === HORIZON_END_OF_DAY)
        return 'EOD';
    if (horizonSeconds === HORIZON_NEXT_DAY_OPEN)
        return 'Next Open';
    return null;
}
/**
 * Get tooltip description for event horizons.
 * Useful for UI components that need to explain what these horizons mean.
 *
 * @param horizonSeconds - The horizon value
 * @returns Descriptive tooltip text, or null for offset horizons
 */
export function getEventHorizonTooltip(horizonSeconds) {
    if (horizonSeconds === HORIZON_END_OF_DAY) {
        return 'Performance at market close (3:59pm EST)';
    }
    if (horizonSeconds === HORIZON_NEXT_DAY_OPEN) {
        return 'Performance at next day market open (9:32am EST)';
    }
    return null;
}
/**
 * Sort horizons with offset horizons first (ascending), then event horizons.
 * EOD comes before Next Open in the event horizon section.
 *
 * @param horizons - Array of horizon values to sort
 * @returns New sorted array
 */
export function sortHorizons(horizons) {
    return [...horizons].sort((a, b) => {
        const isAEventHorizon = isEventHorizon(a);
        const isBEventHorizon = isEventHorizon(b);
        // Event horizons go after offset horizons
        if (isAEventHorizon && !isBEventHorizon)
            return 1;
        if (!isAEventHorizon && isBEventHorizon)
            return -1;
        // Within event horizons, sort by value descending (EOD=2147483647 before NDO=2147483646)
        if (isAEventHorizon && isBEventHorizon)
            return b - a;
        // Within offset horizons, sort ascending
        return a - b;
    });
}
// ============================================================================
// Fixed Horizon Menu (offset horizons per bar duration)
// ============================================================================
/**
 * Map of bar durations to their equivalent in seconds.
 *
 * Keyed by string rather than `BarDuration` because it also covers legacy
 * duration keys (`1d_ah`, `1d_noah`) that stored configs may still reference.
 */
export const DURATION_SECONDS = {
    '1m': 60,
    '3m': 180,
    '5m': 300,
    '15m': 900,
    '30m': 1800,
    '60m': 3600,
    '120m': 7200,
    '180m': 10800,
    '240m': 14400,
    '1d': 86400,
    '1d_ah': 86400, // Same as 1d for horizon calculation
    '1d_noah': 86400, // Same as 1d for horizon calculation
    '1w': 604800,
    '1mo': 2592000
};
/**
 * Fallback horizon set historically used for unknown durations
 * (ultra-short-term). Exposed so callers that want that behavior can preserve
 * it explicitly — `getTimeHorizonsForDuration` itself never falls back.
 */
export const ULTRA_SHORT_TERM_HORIZONS = [300, 900, 1800, 3600, 7200, 14400];
/** Optional calendar-day holds for intraday swing entries. Defaults stay bounded. */
export const INTRADAY_SWING_HORIZONS = [86400, 172800, 259200, 604800, 1209600, 2592000, 7776000];
/** All supported fixed horizons; includes opt-in holds without expanding job defaults. */
export function getSupportedTimeHorizonsForDuration(duration) {
    const defaults = getTimeHorizonsForDuration(duration);
    if (defaults === null)
        return null;
    if (duration === '1d' || duration === '1d_ah' || duration === '1d_noah') {
        return [...defaults, 5184000].sort((left, right) => left - right);
    }
    return duration === '5m' || duration === '15m'
        ? [...new Set([...defaults, ...INTRADAY_SWING_HORIZONS])].sort((left, right) => left - right)
        : defaults;
}
/**
 * Generate the offset time horizons (in seconds) evaluated for a trading
 * duration. Single source of truth for the TypeScript side; the Rust mirror
 * `project314-backend rust/shared/types/src/time_horizons.rs` matches this
 * menu exactly (Rust cannot consume this package) — keep them in sync.
 *
 * Returns null for unknown durations — pure data, callers decide their own
 * policy. project314-backend `src/helpers/experiments/timeHorizonGenerator.ts`
 * throws; project314-frontend `src/utils/timeHorizonGenerator.ts` falls back
 * to `ULTRA_SHORT_TERM_HORIZONS` because UI render paths must not crash on
 * legacy stored durations.
 *
 * @param duration - The bar duration being tested
 * @returns Array of time horizons in seconds, or null for unknown durations
 */
export function getTimeHorizonsForDuration(duration) {
    const seconds = DURATION_SECONDS[duration];
    if (!seconds) {
        return null;
    }
    // A tier is shared by several durations, so its shortest horizons can be
    // shorter than the entry bar of its longest duration. Such a horizon is
    // structurally degenerate: the end time falls inside the entry bar itself,
    // so end-bar selection resolves back to the entry bar and every sample is
    // exactly 0%. Filtering them here makes the menu per-duration: 60m starts
    // at 3600 rather than the tier's 1800, and 1mo starts at its own bar
    // length. Every other duration already opened at or above its bar length.
    return horizonTierForDurationSeconds(seconds).filter((horizonSeconds) => horizonSeconds >= seconds);
}
/**
 * The shared horizon tier for a bar length, before the per-duration
 * no-horizon-shorter-than-the-bar filter in `getTimeHorizonsForDuration`.
 */
function horizonTierForDurationSeconds(seconds) {
    // Ultra short-term (1m-5m): Test up to 4 hours
    // These are scalping timeframes - need quick evaluation
    if (seconds <= 300) {
        return [
            300, // 5m - Quick scalp exit
            900, // 15m - Short hold
            1800, // 30m - Medium hold
            3600, // 1h - Extended hold
            7200, // 2h - Long hold
            14400 // 4h - Maximum reasonable hold
        ];
    }
    // Short-term (15m-60m): Test up to 2 weeks
    // Day trading timeframes - need intraday, overnight, and swing evaluation
    else if (seconds <= 3600) {
        return [
            1800, // 30m - Quick exit
            3600, // 1h - Short hold
            7200, // 2h - Medium hold
            14400, // 4h - Half day hold
            28800, // 8h - Full trading day
            86400, // 1d - Overnight hold
            172800, // 2d - Multi-day hold
            259200, // 3d - Three day hold
            604800, // 1w - Weekly hold
            1209600 // 2w - Two week hold
        ];
    }
    // Medium-term (2h-4h): Test up to 2 weeks
    // Swing trading timeframes - need multi-day evaluation
    else if (seconds <= 14400) {
        return [
            14400, // 4h - Same day exit
            28800, // 8h - Intraday hold
            86400, // 1d - Daily hold
            172800, // 2d - Two day hold
            259200, // 3d - Three day hold
            604800, // 1w - Weekly hold
            1209600 // 2w - Two week hold
        ];
    }
    // Daily (1d): Test up to 3 months
    // Position trading timeframes - need weekly and monthly evaluation
    else if (seconds <= 86400) {
        return [
            86400, // 1d - Same day (for 1d bars, this is immediate)
            172800, // 2d - Two day hold
            259200, // 3d - Three day hold
            604800, // 1w - Weekly hold
            1209600, // 2w - Two week hold
            2592000, // 1mo - Monthly hold
            7776000 // 3mo - Quarterly hold
        ];
    }
    // Weekly+ (1w, 1mo): Test up to 1 year
    // Long-term investing timeframes - need monthly and quarterly evaluation
    else {
        return [
            604800, // 1w - Weekly check
            1209600, // 2w - Bi-weekly
            2592000, // 1mo - Monthly
            5184000, // 2mo - Two months
            7776000, // 3mo - Quarterly
            15552000, // 6mo - Semi-annual
            31536000 // 1y - Annual
        ];
    }
}
//# sourceMappingURL=ExperimentTypes.js.map